import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faChalkboardTeacher,
  faRunning,
  faUser,
  faIdBadge,
} from "@fortawesome/free-solid-svg-icons";

// Utility untuk caching
const cachePesananDetail = (uid, eventId, orderId, data) => {
  try {
    localStorage.setItem(
      `pesanan_detail_${uid}_${eventId}_${orderId}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("Failed to save cache:", err);
  }
};

const getCachedPesananDetail = (uid, eventId, orderId) => {
  try {
    const cached = localStorage.getItem(
      `pesanan_detail_${uid}_${eventId}_${orderId}`
    );
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000; // 5 minutes
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`pesanan_detail_${uid}_${eventId}_${orderId}`);
      return null;
    }
    return data;
  } catch (err) {
    console.warn("Failed to retrieve cache:", err);
    localStorage.removeItem(`pesanan_detail_${uid}_${eventId}_${orderId}`);
    return null;
  }
};

// --- Definisi Komponen ---
export const OrderDetail = React.memo(() => {
  // --- State dan Variabel ---
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pesanan, setPesanan] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const eventId = searchParams.get("eventId");
  const orderId = searchParams.get("orderId");
  const status = searchParams.get("transaction_status");

  // --- Fungsi Pembantu ---
  const formatDate = useCallback((date) => {
    if (!date) return "Not available";
    try {
      if (date && (date.seconds || date._seconds)) {
        const timestamp = date.seconds || date._seconds;
        return new Date(timestamp * 1000).toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      if (date.toDate && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      if (typeof date === "string") {
        let parsedDate = new Date(date.replace(" at ", ""));
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
        parsedDate = new Date(date);
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
      }
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      console.warn("Date format not recognized:", date);
      return "Not available";
    } catch (err) {
      console.error("Failed to format date:", err, date);
      return "Not available";
    }
  }, []);

  const formatCategory = useCallback((pesananData, eventData) => {
    if (eventData?.type === "seminar" || eventData?.type === "membership") {
      return null;
    }
    if (pesananData?.kategori && pesananData.kategori !== "") {
      // Jika kategori mengandung tanda "-", ambil bagian setelahnya
      if (pesananData.kategori.includes("-")) {
        return pesananData.kategori.split("-")[1].trim();
      }
      return pesananData.kategori;
    }
    if (eventData?.categories?.length > 0) {
      const category =
        eventData.categories.find(
          (cat) => cat.distance === pesananData?.distance
        ) || eventData.categories[0];
      const parts = [];
      if (category?.distance) parts.push(`${category.distance} km`);
      if (category?.cot) parts.push(`COT ${category.cot} hours`);
      return parts.join(", ") || "Unknown";
    }
    return "Unknown";
  }, []);

  const fetchPesananDetail = useCallback(
    async (forceRefresh = true) => {
      if (
        !user?.uid ||
        !eventId ||
        !orderId ||
        fetchStatus.current.isFetching
      ) {
        if (!user?.uid) setError("User not authenticated. Please login again.");
        if (!eventId || !orderId)
          setError("Invalid eventId or orderId parameter.");
        return;
      }

      fetchStatus.current.isFetching = true;
      try {
        setError("");

        // Hapus cache lama untuk memastikan data baru diambil
        localStorage.removeItem(
          `pesanan_detail_${user.uid}_${eventId}_${orderId}`
        );

        const collectionName =
          eventId === "ALTI_MEMBERSHIP"
            ? "daftarAnggotaAlti"
            : `pendaftaran${eventId}`;
        const pesananQuery = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("__name__", "==", orderId)
        );
        const pesananSnapshot = await getDocs(pesananQuery);

        if (pesananSnapshot.empty) {
          throw new Error("Order not found or you do not have access.");
        }

        const pesananDoc = pesananSnapshot.docs[0];
        const pesananData = pesananDoc.data();

        let eventData = null;
        if (eventId === "ALTI_MEMBERSHIP") {
          eventData = {
            title: "ALTI Membership",
            location: "Jakarta",
            eventDateTimestamp: null,
            type: "membership",
          };
        } else {
          const eventDocRef = doc(db, "semuaKegiatan", eventId);
          const eventDoc = await getDoc(eventDocRef);
          if (!eventDoc.exists()) {
            throw new Error(`Event ${eventId} not found.`);
          }
          eventData = eventDoc.data();

          if (eventData.categories?.length > 0) {
            eventData.eventDate = eventData.categories[0].date || null;
          }

          if (!eventData.title || !eventData.location || !eventData.type) {
            throw new Error(
              `Event ${eventId} data is incomplete. Please contact support.`
            );
          }
          if (
            eventData.type === "seminar" &&
            eventData.categories?.length > 0
          ) {
            eventData.eventDateTimestamp = eventData.categories[0].date || null;
          }
          eventData.eventDateTimestamp = eventData.eventDateTimestamp || null;
        }

        if (isMounted.current) {
          console.log("[OrderDetail] Raw pesananData:", pesananData);
          console.log("[OrderDetail] bibNumber field:", pesananData.bibNumber);
          const pesananFormatted = {
            id: pesananDoc.id,
            eventId,
            collectionName,
            kategori: formatCategory(pesananData, eventData),
            harga: pesananData.harga || 0,
            bibNumber: pesananData.bibNumber || null,
            ...pesananData,
          };
          console.log("[OrderDetail] pesananFormatted:", pesananFormatted);
          setPesanan(pesananFormatted);
          setEventData(eventData);
          cachePesananDetail(user.uid, eventId, orderId, {
            pesanan: pesananFormatted,
            eventData,
          });
        }
      } catch (err) {
        if (isMounted.current) {
          setError(
            err.message.includes("permission-denied") ||
              err.message.includes("insufficient permissions")
              ? "Access denied: You do not have permission to view this order."
              : err.message ||
                  "Failed to load order details. Please try again later."
          );
          showNotification(
            "error",
            err.message.includes("permission-denied") ||
              err.message.includes("insufficient permissions")
              ? "Access denied: You do not have permission."
              : err.message || "Failed to load order details."
          );
        }
      } finally {
        fetchStatus.current.isFetching = false;
      }
    },
    [user, eventId, orderId, showNotification, formatCategory]
  );

  const handleBayar = useCallback(
    (snapToken) => {
      if (snapToken && window.snap) {
        window.snap.pay(snapToken, {
          onSuccess: () => {
            showNotification("success", "Payment successful!");
            fetchPesananDetail(true);
          },
          onPending: () => {
            showNotification("warning", "Payment is being processed.");
            fetchPesananDetail(true);
          },
          onError: () => {
            setError("An error occurred during payment.");
            showNotification("error", "An error occurred during payment.");
          },
          onClose: () => {},
        });
      } else {
        setError("Payment token or Midtrans not found.");
        showNotification("error", "Payment token or Midtrans not found.");
      }
    },
    [showNotification, fetchPesananDetail]
  );

  // --- Efek Samping (useEffect) ---
  useEffect(() => {
    isMounted.current = true;
    if (!user) {
      navigate("/login");
      showNotification("error", "Please login to view order details.");
      return;
    }

    fetchPesananDetail();

    if (status === "settlement") {
      setMessage({ type: "success", text: "Payment successful!" });
      showNotification("success", "Payment successful!");
    } else if (status === "pending") {
      setMessage({ type: "warning", text: "Please wait for confirmation." });
      showNotification("warning", "Please wait for confirmation.");
    } else if (status === "failed") {
      setMessage({
        type: "error",
        text: "Payment failed. Please try again.",
      });
      showNotification("error", "Payment failed.");
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, status, showNotification, fetchPesananDetail, navigate]);

  // --- Render JSX ---
  return (
    <section className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[450px]">
        {/* Notifications */}
        <AnimatePresence>
          {message && (
            <div
              className={`fixed top-4 right-4 p-4 rounded-xl flex items-center space-x-2 w-11/12 sm:w-auto max-w-md z-50 ${
                message.type === "success"
                  ? "bg-green-100 text-green-800"
                  : message.type === "warning"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <FontAwesomeIcon
                icon={
                  message.type === "success"
                    ? faCheckCircle
                    : message.type === "warning"
                    ? faClock
                    : faTimesCircle
                }
                className="h-5 w-5"
              />
              <span>{message.text}</span>
            </div>
          )}
        </AnimatePresence>

        {error ? (
          <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
            <div className="text-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-8 w-8 text-red-600 mb-4"
              />
              <p className="text-red-600 text-base font-medium">{error}</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden">
            {/* Header Section */}
            <div className="text-center">
              <FontAwesomeIcon
                icon={
                  eventData?.type === "trail_running"
                    ? faRunning
                    : eventData?.type === "membership"
                    ? faUser
                    : faChalkboardTeacher
                }
                className="h-12 w-12 text-slate-700 my-4"
              />
              <h2 className="text-3xl font-bold text-slate-700">
                {eventData?.title || pesanan?.eventId || "Loading data..."}
              </h2>

              <div className="mt-1 text-cyan-600 text-sm">
                <span>{eventData?.location || "Loading data..."}</span>
              </div>
            </div>

            {/* Status Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-700">
                  Order Status
                </h3>
                <span
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full border ${
                    pesanan?.transaction_status === "settlement"
                      ? "border-green-600 text-green-700"
                      : pesanan?.transaction_status === "pending"
                      ? "border-yellow-500 text-yellow-600"
                      : "border-gray-500 text-gray-600"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={
                      pesanan?.transaction_status === "settlement"
                        ? faCheckCircle
                        : pesanan?.transaction_status === "pending"
                        ? faClock
                        : faTimesCircle
                    }
                    className={
                      pesanan?.transaction_status === "settlement"
                        ? "text-green-600"
                        : pesanan?.transaction_status === "pending"
                        ? "text-yellow-500"
                        : "text-gray-500"
                    }
                  />
                  {pesanan?.transaction_status === "settlement"
                    ? "Success"
                    : pesanan?.transaction_status === "pending"
                    ? "Pending"
                    : "Loading data..."}
                </span>
              </div>

              {eventData?.type === "trail_running" && (
                <p className="text-sm text-gray-600 mt-2">
                  <span className="font-bold text-slate-700">Category:</span>{" "}
                  {pesanan?.kategori || "Loading data..."}
                </p>
              )}
              {(pesanan?.createdAt || pesanan?.transaction_time) && (
                <p className="text-sm text-gray-600 mt-2">
                  <span className="font-bold text-slate-700">
                    Transaction Time:
                  </span>{" "}
                  {formatDate(pesanan?.createdAt || pesanan?.transaction_time)}
                </p>
              )}
              {pesanan?.transaction_status === "pending" &&
                pesanan?.va_numbers?.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-100 rounded-xl">
                    <h4 className="text-sm font-bold text-cyan-600">
                      Payment Details
                    </h4>
                    <ul className="mt-2 space-y-2 text-sm text-gray-600">
                      {pesanan.va_numbers.map((va, index) => (
                        <li key={index}>
                          <span className="font-medium text-gray-600">
                            {va.bank?.toUpperCase() || "Bank"} VA:
                          </span>{" "}
                          {va.va_number || "Loading data..."}
                        </li>
                      ))}
                    </ul>
                    {pesanan.expiry_time && (
                      <p className="text-sm text-red-600 mt-2">
                        <span className="font-medium">Expiry Time:</span>{" "}
                        {formatDate(pesanan.expiry_time)}
                      </p>
                    )}
                  </div>
                )}
            </div>

            {/* Registrant Details Section */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-slate-700 mb-4">
                Registrant Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: faUser,
                    label: "Name",
                    value: pesanan?.namaLengkap || "Loading data...",
                  },
                  {
                    icon: faUser,
                    label: "Order ID",
                    value:
                      pesanan?.orderId?.replace(/^(ORDER-|MEMBERSHIP-)/, "") ||
                      "Loading data...",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-3 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="h-4 w-4 text-cyan-600"
                      />
                      <span className="text-sm font-bold text-cyan-600">
                        {item.label}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 ml-6">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* BIB Number - Full width centered */}
              {pesanan?.bibNumber && pesanan?.transaction_status === "settlement" && (
                <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    BIB Number
                  </span>
                  <div className="text-5xl font-black text-cyan-600 tracking-widest mt-2">
                    {pesanan.bibNumber}
                  </div>
                </div>
              )}
            </div>

            {/* Pay Now Button */}
            <div className="flex justify-center my-4">
              {pesanan?.transaction_status === "pending" &&
                pesanan?.snapToken && (
                  <button
                    onClick={() => handleBayar(pesanan.snapToken)}
                    className="px-4 py-3 rounded-xl text-sm uppercase font-medium bg-cyan-600 text-white hover:bg-cyan-700 transition-colors duration-200 w-full"
                    aria-label="Pay now"
                  >
                    Pay Now
                  </button>
                )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
});
